import { createContext, useContext, useState, useRef } from "react";
import { Stack, Box, Typography } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import QuestionMarkOutlinedIcon from "@mui/icons-material/QuestionMarkOutlined";
import { Popup } from "@grapecity/wijmo.react.input";
import { styleExplorer } from "qubCommon/qubStyles";
import "@grapecity/wijmo.styles/wijmo.css";
import ButtonFreeText from "components/Buttons/ButtonFreeText";
import { FlexGrid } from "@grapecity/wijmo.react.grid";
import colors from "assets/scss/colors.scss";
import { useEffect } from "react";

const ModalImages = {
  Warning: 1,
  Question: 2,
  Error: 3,
  Info: 4,
};

const ModalButtons = {
  OkCancel: 1,
  YesNo: 2,
  YesNoCancel: 3,
  OK: 4,
  Yes: 5,
  No: 6,
  Cancel: 7,
};

const ModalContext = createContext({});

const ModalImage = ({ imageType }) => {
  if (imageType === ModalImages.Error)
    return <ErrorIcon style={{ fontSize: 54, color: "red" }} />;
  else if (imageType === ModalImages.Warning)
    return <WarningIcon style={{ width: 54, color: "orange" }} />;
  else if (imageType === ModalImages.Info)
    return <InfoIcon style={{ width: 54, color: "blue" }} />;
  else if (imageType === ModalImages.Question)
    return <QuestionMarkOutlinedIcon style={{ width: 54, color: "blue" }} />;
  else return <></>;
};

function getbuttonsArray(buttons) {
  let buttonsArray;
  switch (buttons) {
    case ModalButtons.YesNo:
      buttonsArray = [
        { label: "Ναι", command: ModalButtons.Yes },
        { label: "Οχι", command: ModalButtons.No },
      ];
      break;
    case ModalButtons.OK:
      buttonsArray = [{ label: "Ok", command: ModalButtons.OK }];
      break;
    case ModalButtons.OkCancel:
      buttonsArray = [
        { label: "Ok", command: ModalButtons.OK },
        { label: "Cancel", command: ModalButtons.Cancel },
      ];
      break;
    case ModalButtons.YesNoCancel:
      buttonsArray = [
        { label: "Ναι", command: ModalButtons.Yes },
        { label: "Οχι", command: ModalButtons.No },
        { label: "Ακύρωση", command: ModalButtons.Cancel },
      ];
      break;
    default:
      buttonsArray = [{ label: "Ok", command: ModalButtons.OK }];
      break;
  }

  return buttonsArray;
}

function ModalProvider({ children }) {
  const [title, setTitle] = useState(null);
  const [message, setMessage] = useState("");
  const [imageType, setImageType] = useState(null);
  const [defaultButton, setDefaultButton] = useState(null);
  const [isGrid, setIsGrid] = useState(false);
  const [gridData, setGridData] = useState(null);
  const popupRef = useRef();

  const popupInitialized = (popup) => {
    popup.isDraggable = true;
    popup.isResizable = true;
    popup.fadeOut = false;
    popup.fadeIn = false;

    popupRef.current = popup;
  };

  const gridLoadedRows = (flex) => {
    // console.log("mpika");
    flex.autoSizeColumns();
  };

  const [buttons, setButtons] = useState([]);
  const ref = useRef();

  function openGrid(props) {
    setIsGrid(true);
    const { title, gridData } = props;
    setTitle(title);
    setGridData(gridData);

    popupRef.current.show(true);
  }
  function openModal(props) {
    const { title, message, imageType, buttons, callback, defaultButton } =
      props;

    ref.current = {
      callback,
    };
    setIsGrid(false);
    setDefaultButton(defaultButton);
    setButtons(getbuttonsArray(buttons));
    setTitle(title);
    setMessage(message);
    setImageType(imageType);
  }

  useEffect(() => {
    if (title && message && imageType) popupRef.current.show(true);
  }, [title, message, imageType]);

  const handleButtonClick = (command) => {
    setTitle(null);
    popupRef.current.hide();
    ref.current.callback(command);
    setIsGrid(false);
  };

  return (
    <ModalContext.Provider value={{ openModal, openGrid }}>
      <Popup initialized={popupInitialized} hideTrigger={0}>
        <Stack sx={styleExplorer.main}>
          <div
            className="wj-dialog-header Qub-DatagridBringValueView"
            style={{ backgroundColor: colors.bringValueHeader }}
          >
            <div>
              <h6 style={{ marginBottom: 0, marginTop: 0 }}>{title}</h6>
            </div>
            <button
              type="button"
              style={{ cursor: "pointer" }}
              tabIndex={-1}
              className="close wj-hide"
            >
              &times;
            </button>
          </div>
          {!isGrid ? (
            <>
              <Box sx={styleExplorer.content}>
                <Stack direction="row" alignItems="center">
                  <ModalImage imageType={imageType} />
                  <Box
                    sx={{
                      p: 1,
                      whiteSpace: "pre-wrap",
                      overflowWrap: "break-word",
                    }}
                  >
                    <Typography>{message}</Typography>
                  </Box>
                </Stack>
              </Box>

              <Stack
                direction="row"
                justifyContent="center"
                spacing={2}
                marginBottom={2}
              >
                {buttons.map((b) => {
                  return (
                    <ButtonFreeText
                      sx={{
                        "&:focus": {
                          border: "solid",
                        },
                      }}
                      autoFocus={
                        buttons?.length === 1 || b?.command === defaultButton
                      }
                      key={b.label}
                      handleClick={() => handleButtonClick(b.command)}
                      text={b.label}
                    />
                  );
                })}
              </Stack>
            </>
          ) : (
            <FlexGrid loadedRows={gridLoadedRows} itemsSource={gridData} />
          )}
        </Stack>
      </Popup>

      {children}
    </ModalContext.Provider>
  );
}

function useModal() {
  const modalContext = useContext(ModalContext);
  if (!modalContext) {
    throw new Error("useModal must be used within LoadingProvider");
  }

  return modalContext;
}

export { ModalProvider, useModal, ModalButtons, ModalImages };

// openModal({
//   title: "SampleTitle",
//   message:
//     "SampleMessageSamplmpleMessageSampleMessSampleMessageSampleMessageSampleMessageSampleMessageSampleMessage",
//   imageType: ModalImages.Error,
//   buttons: ModalButtons.YesNo,
//   callback: (command) => {
//     if (command === ModalButtons.Yes) successfulLogin();
//     else console.log("You pressed " + command);
//   },
// });
